import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getMyMonster } from "@/lib/game/monster.functions";
import { Button } from "@/components/ui/button";
import { useAuthReady } from "@/hooks/use-auth-ready";

export const Route = createFileRoute("/_app/game/")({
  component: GameGate,
});

function GameGate() {
  const { t } = useTranslation("game");
  const navigate = useNavigate();
  const fetchMon = useServerFn(getMyMonster);
  const { isReady, userId } = useAuthReady();

  const { data, isFetching, isFetched, error, refetch } = useQuery({
    queryKey: ["my-monster", userId],
    queryFn: () => fetchMon(),
    enabled: isReady && !!userId,
    staleTime: 10_000,
    gcTime: 0,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
  });

  useEffect(() => {
    // Only act once the auth session is hydrated AND a finished fetch result exists.
    // Otherwise an empty `data` from a not-yet-run query could falsely redirect to onboarding.
    if (!isReady || !userId) return;
    if (isFetching || !isFetched || error || !data) return;

    if (data.monster) {
      navigate({ to: "/game/battle", replace: true });
    } else if (data.hasStored) {
      console.info("[GameGate] no active monster but stored ones exist → battle");
      navigate({ to: "/game/battle", replace: true });
    } else {
      console.info("[GameGate] no monster at all → onboarding");
      navigate({ to: "/game/onboarding", replace: true });
    }
  }, [isReady, userId, isFetching, isFetched, error, data, navigate]);

  if (error) {
    console.error("[GameGate] getMyMonster failed", error);
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4 text-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <p className="text-sm text-muted-foreground">
          {t("appShell.dbSlowRetrying")}
        </p>
        <Button variant="outline" onClick={() => refetch()}>
          {t("appShell.retryButton")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  );
}
